import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostListener, OnInit } from '@angular/core';

import { BehaviorSubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import {
    BaseContainer,
    DialogConfig,
    DialogRef,
    DottedKey,
    MultiFormSelectOptions,
    ThesaurusConceptReference
} from '@stylehaven/components';
import {
    ThesaurusTreeSelectionMode,
    ThesaurusTreeSelectionMulti,
    ThesaurusTreeSelectionSingle
} from '../../common/entities/common/thesaurus-tree-selection.entity';
import {
    ThesaurusTreeDialogOutput,
    ThesaurusTreeDialogOutputCancel,
    ThesaurusTreeDialogOutputSuccess
} from '../dialog.module';

@Component({
    selector: 'med-thesaurus-tree-dialog',
    templateUrl: './thesaurus-tree-dialog.component.html',
    changeDetection: ChangeDetectionStrategy.Default
})
export class ThesaurusTreeDialogComponent extends BaseContainer implements OnInit {
    public thesaurusDefDottedKey: DottedKey;
    public selectionMode: ThesaurusTreeSelectionMode;
    public enableSuggestions: boolean;
    public selectMultiple: boolean;
    public id: string;
    public readonly: boolean;
    public initialSuggestions: string[];
    public initialTags: MultiFormSelectOptions<ThesaurusConceptReference>;
    public allowEmptyResult: boolean;
    public disableSubmit: boolean = true;

    public concepts$: BehaviorSubject<MultiFormSelectOptions<ThesaurusConceptReference>> = new BehaviorSubject<
        MultiFormSelectOptions<ThesaurusConceptReference>
    >({ options: [], isLiveData: false });

    public concept$: BehaviorSubject<ThesaurusConceptReference | null> =
        new BehaviorSubject<ThesaurusConceptReference | null>(null);

    public suggestions$: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);

    constructor(
        public config: DialogConfig,
        public dialog: DialogRef<ThesaurusTreeDialogOutput>,
        private readonly cd: ChangeDetectorRef
    ) {
        super();
    }

    @HostListener('document:keydown.enter', ['$event'])
    handleEnter(event: { target: HTMLInputElement }) {
        // Prevent submitting when form is invalid
        if (this.disableSubmit) {
            return;
        }
        this.submit();
    }

    ngOnInit(): void {
        this.initThesaurusTree();
    }

    private initThesaurusTree(): void {
        this.thesaurusDefDottedKey = this.config.data?.thesaurusDefDottedKey;
        this.enableSuggestions = this.config.data?.enableSuggestions ?? false;
        this.selectMultiple = this.config.data?.selectMultiple ?? false;
        this.selectionMode = this.config.data?.selectionMode ?? ThesaurusTreeSelectionMode.ParentSelection;
        this.initialSuggestions = this.config.data?.initialSuggestions ?? [];
        this.initialTags = this.config.data?.initialTags ?? { options: [], isLiveData: false };
        this.readonly = this.config.data?.readonly ?? true;
        this.id = this.config.data?.id ?? '';
        this.allowEmptyResult = this.config.data?.allowEmptyResult ?? false;

        this.initSubmitStateListeners();
    }

    private initSubmitStateListeners(): void {
        if (this.allowEmptyResult) {
            this.disableSubmit = false;
            return;
        }

        if (this.selectMultiple) {
            this.concepts$
                .pipe(takeUntil(this.destroy$))
                .subscribe(concepts => (this.disableSubmit = concepts.options.length === 0));
        } else {
            this.concept$.pipe(takeUntil(this.destroy$)).subscribe(concept => {
                this.disableSubmit = concept === null;
                this.cd.detectChanges();
            });
        }
    }

    submit(): void {
        let output: ThesaurusTreeDialogOutputSuccess;
        if (this.selectMultiple) {
            const selection: ThesaurusTreeSelectionMulti = {
                concepts: this.concepts$.value.options.map(option => option.value),
                suggestions: this.suggestions$.value,
                type: 'multi'
            };
            output = { type: 'success', selection };
        } else {
            const selection: ThesaurusTreeSelectionSingle = {
                concept: this.concept$.value,
                suggestions: this.suggestions$.value,
                type: 'single'
            };
            output = { type: 'success', selection };
        }

        this.dialog.close(output);
    }

    cancel(): void {
        const cancel: ThesaurusTreeDialogOutputCancel = { type: 'cancel' };
        this.dialog.close(cancel);
    }

    onConceptsChanged(selectedConcepts: MultiFormSelectOptions<ThesaurusConceptReference>) {
        this.concepts$.next(selectedConcepts);
    }

    onConceptChanged(selectedConcept: ThesaurusConceptReference | null) {
        this.concept$.next(selectedConcept);
    }

    onSuggestionsChanged(selectedSuggestions: string[]) {
        this.suggestions$.next(selectedSuggestions);
    }
}
